<template>
	<div>
		<div class="p-d-flex p-jc-center">
			<h1 class="p-text-bold p-my-0">Step 5: Verify and Pay</h1>
		</div>

		<Transition name="fade" mode="out-in">
			<div v-if="!proceedBtnLoading" key="summary">
				<Message
					v-show="store.formSubmissionData.errorMessage != null"
					severity="error"
				>
					{{ store.formSubmissionData.errorMessage }}
				</Message>

				<h2 class="p-mb-0 p-mt-2 p-primary-color p-text-bold">
					Total: ${{
						store.ProgramSelection.totalOfAllChildren != undefined
							? store.ProgramSelection.totalOfAllChildren.toFixed(2)
							: 0
					}}
					(CAD)
				</h2>

				<div class="p-fluid p-grid p-formgrid p-jc-center p-mt-3">
					<div v-if="!isMobile">
						<div>
							<Summary />

							<div class="p-col-12 p-lg-10 p-field p-mt-2 p-text-left">
								<label class="p-text-bold" for="heardAboutProgram">
									How did you hear about the programs?
								</label>

								<Textarea
									v-model="heardAboutProgram"
									placeholder="How did you hear about the programs? (optional)"
								/>
							</div>
						</div>
					</div>

					<div v-else-if="isMobile" class="p-col-12 p-sm-8 p-md-7 p-mb-3">
						<Accordion :activeIndex="1">
							<AccordionTab>
								<template #header>
									<h4 class="p-text-bold p-my-0">Summary</h4>
								</template>

								<Summary />
							</AccordionTab>
						</Accordion>
					</div>

					<div class="p-col-12 p-sm-8 p-md-7 p-lg-6 p-text-left">
						<h3
							class="p-mt-0"
							:class="
								isMobile
									? 'p-col-12  p-text-bold p-mb-3'
									: 'p-col-12  p-text-bold p-mb-3'
							"
							style="text-decoration: underline"
						>
							Payment Details
						</h3>

						<div class="p-col-12 p-field p-mb-3">
							<label for="cardNumber" class="p-text-bold">Card Number *</label>

							<div class="p-inputgroup">
								<InputText
									id="cardNumber"
									v-model="cardNumber"
									placeholder="Card Number"
									v-mask="
										cardType == 'AMEX'
											? '#### ###### #####'
											: '#### #### #### ####'
									"
									inputmode="numeric"
								/>

								<span
									id="cardNumber"
									class="p-inputgroup-addon p-py-0"
									style="background: transparent"
								>
									<CardVisa v-if="cardType === 'Visa'" />
									<CardMastercard v-else-if="cardType === 'Mastercard'" />
									<CardDiscover v-else-if="cardType === 'Discover'" />
									<CardAmericanExpress v-else-if="cardType === 'AMEX'" />
								</span>
							</div>

							<span v-if="v$.cardNumber.$error && submitted">
								<span
									id="cardNumber-error"
									v-for="(error, index) of v$.cardNumber.$errors"
									:key="index"
								>
									<small class="p-error p-text-bold">
										{{
											error.$message == "Value is required"
												? error.$message.replace("Value", "Card Number")
												: cardType == "AMEX"
												? error.$message.replace(
														"This field should be at least 17 characters long",
														"Invalid card number"
												  )
												: error.$message.replace(
														"This field should be at least 19 characters long",
														"Invalid card number"
												  )
										}}
									</small>
								</span>
							</span>
						</div>

						<div class="p-d-flex p-flex-row">
							<div class="p-field p-mb-3 p-col-8 p-text-left">
								<label for="cardExpiryDate" class="p-text-bold">
									Expiry Date *
								</label>

								<InputMask
									id="cardExpiryDate"
									v-model="cardExpiry"
									placeholder="MM/YY"
									mask="99/99"
									slotChar=""
									inputmode="numeric"
								/>

								<span v-if="v$.cardExpiry.$error && submitted">
									<span
										id="cardExpiry-error"
										v-for="(error, index) of v$.cardExpiry.$errors"
										:key="index"
									>
										<small class="p-error p-text-bold">
											{{
												error.$message == "Value is required"
													? error.$message.replace("Value", "Expiry Date")
													: error.$message.replace(
															"This field should be at least 5 characters long",
															"Invalid expiry date"
													  )
											}}
										</small>
									</span>
								</span>
							</div>

							<div class="p-field p-mb-3 p-col-4 p-text-left">
								<label for="cvv" class="p-text-bold">CVV *</label>

								<InputText
									id="cvv"
									v-model="cvv"
									placeholder="CVV"
									v-mask="'####'"
									inputmode="numeric"
								/>

								<small
									v-if="v$.cvv.$invalid && submitted"
									class="p-error p-text-bold"
								>
									{{ v$.cvv.required.$message.replace("Value", "CVV") }}
								</small>
							</div>
						</div>

						<div class="p-field p-mb-3 p-col-12 p-text-left">
							<label for="cardholderName" class="p-text-bold">
								Cardholder Name *
							</label>

							<InputText
								id="cardholderName"
								v-model="cardholderName"
								placeholder="Cardholder Name"
								type="text"
							/>

							<small
								v-if="v$.cardholderName.$invalid && submitted"
								class="p-error p-text-bold"
							>
								{{
									v$.cardholderName.required.$message.replace(
										"Value",
										"Cardholder Name"
									)
								}}
							</small>
						</div>

						<h3
							class="p-col-12 p-text-left p-text-bold p-mt-4 p-mb-3"
							style="text-decoration: underline"
						>
							Billing Details
						</h3>

						<div class="p-d-flex p-flex-column p-flex-md-row">
							<div class="p-field p-mb-3 p-col-12 p-md-6 p-text-left">
								<label for="firstName" class="p-text-bold">
									First Name *
								</label>

								<InputText
									id="firstName"
									v-model="firstName"
									placeholder="First Name"
									type="text"
								/>

								<small
									v-if="v$.firstName.$invalid && submitted"
									class="p-error p-text-bold"
								>
									{{
										v$.firstName.required.$message.replace(
											"Value",
											"First Name"
										)
									}}
								</small>
							</div>

							<div class="p-field p-mb-3 p-col-12 p-md-6 p-text-left">
								<label for="lastName" class="p-text-bold"> Last Name * </label>

								<InputText
									id="lastName"
									v-model="lastName"
									placeholder="Last Name"
									type="text"
								/>

								<small
									v-if="v$.lastName.$invalid && submitted"
									class="p-error p-text-bold"
								>
									{{
										v$.lastName.required.$message.replace("Value", "Last Name")
									}}
								</small>
							</div>
						</div>

						<div class="p-field p-mb-3 p-col-12">
							<label for="address" class="p-text-bold">Address *</label>

							<AutoComplete
								id="address"
								v-model="address"
								field="name"
								placeholder="Address"
								:suggestions="filteredAddresses"
								@complete="searchAddress($event)"
								@item-select="populateFields($event)"
							/>

							<small
								v-if="v$.address.$invalid && submitted"
								class="p-error p-text-bold"
							>
								{{ v$.address.required.$message.replace("Value", "Address") }}
							</small>
						</div>

						<div class="p-field p-mb-3 p-col-12">
							<label for="address2" class="p-text-bold">Address 2</label>
							<InputText
								id="address2"
								v-model="address2"
								placeholder="Address 2"
								type="text"
							/>
						</div>

						<div class="p-field p-mb-3 p-col-12">
							<label for="city" class="p-text-bold">City *</label>

							<InputText
								id="city"
								v-model="city"
								placeholder="City"
								type="text"
							/>

							<small
								v-if="v$.city.$invalid && submitted"
								class="p-error p-text-bold"
							>
								{{ v$.city.required.$message.replace("Value", "City") }}
							</small>
						</div>

						<div class="p-d-flex p-flex-column p-flex-md-row">
							<div class="p-field p-mb-3 p-col-12 p-md-6">
								<label for="province" class="p-text-bold">
									{{
										country == "Canada"
											? "Province *"
											: country.name == "United States"
											? "State *"
											: "Province / State *"
									}}
								</label>

								<div>
									<Dropdown
										id="province"
										v-model="selectedProvince"
										:options="filteredProvinceState"
										optionLabel="name"
										placeholder="Province"
									/>
								</div>
							</div>

							<div class="p-field p-mb-3 p-col-12 p-md-6">
								<label for="postalCode_zip" class="p-text-bold">
									{{
										country == "Canada"
											? "Postal Code *"
											: country.name == "United States"
											? "ZIP Code *"
											: "Postal Code / ZIP Code *"
									}}
								</label>

								<InputMask
									id="postalCode_zip"
									v-model="postalCode_zip"
									:class="postalCode_zip ? 'p-text-uppercase' : ''"
									:placeholder="
										country == 'Canada'
											? 'Postal Code'
											: country.name == 'United States'
											? 'ZIP Code'
											: 'Postal Code / ZIP Code'
									"
									slotChar=""
									mask="a9a 9a9"
								/>

								<small
									v-if="v$.postalCode_zip.$invalid && submitted"
									class="p-error p-text-bold"
								>
									{{
										v$.postalCode_zip.required.$message.replace(
											"Value",
											country == "Canada"
												? "Postal Code"
												: country.name == "United States"
												? "ZIP Code"
												: "Postal Code / ZIP Code"
										)
									}}
								</small>
							</div>
						</div>

						<div class="p-d-flex p-flex-column p-flex-md-row">
							<div class="p-field p-mb-3 p-col-12 p-md-6">
								<label for="email" class="p-text-bold">Email *</label>

								<InputText
									id="email"
									v-model="email"
									placeholder="Email"
									type="text"
								/>

								<span v-if="v$.email.$error && submitted">
									<span
										id="email-error"
										v-for="(error, index) of v$.email.$errors"
										:key="index"
									>
										<small class="p-text-bold p-error">
											{{
												error.$message == "Value is required"
													? error.$message.replace("Value", "Email")
													: error.$message.replace(
															"Value is not a valid",
															"Invalid"
													  )
											}}
										</small>
									</span>
								</span>
							</div>

							<div class="p-col-12 p-md-6 p-field">
								<label class="p-text-bold" for="phone"> Phone Number: *</label>

								<InputText
									id="phone"
									v-model="phone"
									placeholder="Phone Number"
									v-mask="'(###) ###-####'"
									inputmode="tel"
								/>

								<small
									v-if="v$.phone.$invalid && submitted"
									class="p-error p-text-bold"
								>
									{{
										v$.phone.required.$message.replace("Value", "Phone Number")
									}}
								</small>
							</div>
						</div>

						<div v-if="isMobile" class="p-col-12 p-field p-mb-3">
							<label class="p-text-bold" for="heardAboutProgram">
								How did you hear about the programs?
							</label>

							<Textarea
								v-model="heardAboutProgram"
								placeholder="How did you hear about the programs? (optional)"
							/>
						</div>
					</div>
				</div>

				<div class="p-grid p-formgrid p-mt-3 p-jc-center">
					<div class="p-col-12 p-sm-8 p-md-6 p-lg-6 p-xl-6 p-ai-center">
						<div
							class="p-d-flex p-flex-column-reverse p-flex-sm-row p-ai-center p-jc-evenly"
						>
							<div class="p-mb-3">
								<Button
									label="Back"
									icon="pi pi-angle-left"
									class="p-button-secondary"
									style="min-width: 120px; max-width: 120px"
									:disabled="proceedBtnLoading ? 'disabled' : null"
									@click="prevStep"
								/>
							</div>

							<div class="p-mb-3">
								<Button
									label="Proceed to Pay"
									icon="pi pi-check"
									iconPos="right"
									style="min-width: 200px; max-width: 200px"
									:loading="proceedBtnLoading"
									@click="nextStep"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div v-else key="processing">
				<i
					class="pi pi-spin pi-spinner p-primary-color"
					style="font-size: 5rem !important"
				></i>

				<h3 class="p-text-center p-pt-2 p-text-bold">
					Processing your payment...
				</h3>
			</div>
		</Transition>
	</div>
</template>

<script>
import axios from "axios";
import CardVisa from "../../icons/creditCards/CardVisa.vue";
import CardMastercard from "../../icons/creditCards/CardMastercard.vue";
import CardDiscover from "../../icons/creditCards/CardDiscover.vue";
import CardAmericanExpress from "../../icons/creditCards/CardAmericanExpress.vue";
import gmapsInit from "@/utils/gmaps.js";
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, email } from "@vuelidate/validators";
import moment from "moment";
import Summary from "../blocks/Summary.vue";

import { useFormDataStore } from "../../store/formData.js";

export default {
	name: "Confirm",
	data() {
		return {
			proceedBtnLoading: false,
			finalFormSubmission: null,
			heardAboutProgram: null,
			cardholderName: null,
			cardNumber: null,
			cardExpiry: null,
			cvv: null,
			cardType: null,
			submitted: false,
			firstName: null,
			lastName: null,
			address: null,
			address2: null,
			country: "Canada",
			postalCode_zip: null,
			city: null,
			selectedProvince: { name: "Ontario", code: "ON" },
			email: null,
			phone: null,
			filteredCountries: null,
			filteredProvinceState: null,
			autoCompleteService: null,
			geocoder: null,
			filteredAddresses: [],
			streetNumber: null,
			route: null,
			isMobile: false,
		};
	},
	setup() {
		const store = useFormDataStore();

		return {
			store,
			v$: useVuelidate(),
		};
	},
	validations() {
		return {
			cardholderName: { required },
			cardNumber: {
				required,
				minLength: this.cardType == "AMEX" ? minLength(17) : minLength(19),
			},
			cardExpiry: { required, minLength: minLength(5) },
			cvv: { required },
			firstName: { required },
			lastName: { required },
			address: { required },
			country: { required },
			postalCode_zip: { required },
			city: { required },
			selectedProvince: { required },
			email: { required, email },
			phone: { required, minLength: minLength(14) },
		};
	},
	components: {
		CardVisa,
		CardMastercard,
		CardDiscover,
		CardAmericanExpress,
		Summary,
	},
	watch: {
		cardNumber(input) {
			if (input != null) {
				if (input.substring(0, 1) == "4") {
					this.cardType = "Visa";
				} else if (input.substring(0, 1) == "5") {
					this.cardType = "Mastercard";
				} else if (input.substring(0, 1) == "6") {
					this.cardType = "Discover";
				} else if (
					input.substring(0, 2) == "34" ||
					input.substring(0, 2) == "37"
				) {
					this.cardType = "AMEX";
				} else {
					this.cardType = null;
				}
			}
		},

		selectedProvince(input) {
			let options = [];

			this.store.provinceOptions.forEach((option) => {
				options.push(option.name);
			});

			if (typeof input == "string" && !options.includes(input)) {
				this.selectedProvince = null;
			} else if (typeof input == "string" && options.includes(input)) {
				this.selectedProvince =
					this.store.provinceOptions[options.indexOf(input)];
			}
		},
	},
	async beforeMount() {
		this.filteredProvinceState = [...this.store.provinceOptions];

		this.currentScreenWidth();
		window.addEventListener("resize", this.currentScreenWidth);

		let google = await gmapsInit();
		if (google) {
			this.autoCompleteService =
				new window.google.maps.places.AutocompleteService();
			this.geocoder = new window.google.maps.Geocoder();
		} else {
			console.error("Google Maps API error");
		}
	},
	async mounted() {},
	destroyed() {
		window.removeEventListener("resize", this.currentScreenWidth);
	},
	methods: {
		// toggleDetails(event) {
		// 	this.$refs.op.toggle(event);
		// },
		// getScreenSize() {
		// 	console.log(window.innerWidth);
		// 	if (window.innerWidth < 1001) {
		// 		this.isMobile = true;
		// 	} else {
		// 		this.isMobile = false;
		// 	}

		// },
		currentScreenWidth() {
			if (window.innerWidth < 1001) {
				this.isMobile = true;
			} else {
				this.isMobile = false;
			}
		},
		scrollToFirstError() {
			this.$nextTick(() => {
				if (document.querySelector(".p-error")) {
					let domRect = document
						.querySelector(".p-error")
						.getBoundingClientRect();

					window.scrollTo({
						left: domRect.left + document.documentElement.scrollLeft,
						top: domRect.top + document.documentElement.scrollTop - 100,
						behavior: "smooth",
					});
				}
			});
		},
		searchAddress(event) {
			let countrySearchCriteria = ["ca"];

			this.autoCompleteService.getPlacePredictions(
				{
					input: event.query,
					componentRestrictions: { country: countrySearchCriteria },
					types: ["address"],
				},
				this.addressSearchResults
			);
		},
		addressSearchResults(predictions, status) {
			// console.log(predictions);
			// console.log(status);

			if (status == "OK") {
				this.filteredAddresses = [];

				predictions.forEach((place) => {
					this.filteredAddresses.push({
						name: place.description,
						placeId: place.place_id,
						mainText: place.structured_formatting.main_text,
					});
				});
			}
		},
		populateFields(event) {
			this.model = event.value.mainText;

			this.geocoder
				.geocode({
					placeId: event.value.placeId,
				})
				.then((result) => {
					// console.log(result.results[0]);
					const geocodePayload = result.results[0];
					geocodePayload.address_components.forEach((component) => {
						if (component.types.includes("street_number")) {
							this.streetNumber = component.long_name;
						} else if (component.types.includes("route")) {
							this.route = component.long_name;
							this.address = `${this.streetNumber} ${this.route}`;
						} else if (component.types.includes("locality")) {
							this.city = component.long_name;
						} else if (component.types.includes("postal_code")) {
							this.postalCode_zip = component.long_name;
						} else if (
							component.types.includes("administrative_area_level_1")
						) {
							this.selectedProvince =
								this.store.provinceOptions[
									this.store.provinces.indexOf(component.long_name)
								];
						}
					});
				});
		},
		async nextStep() {
			const isFormValid = await this.v$.$validate();
			this.submitted = true;
			if (!isFormValid) {
				this.scrollToFirstError();
			} else {
				this.proceedBtnLoading = true;
				await this.submitForm().then(() => {
					this.proceedBtnLoading = false;

					this.$emit("formSubmitted");
				});
			}
		},
		async submitForm() {
			let token = await this.generateRecaptchaToken();

			let childData = [];
			Object.values(this.store.ChildInfo).forEach((child) => {
				childData.push({
					firstName: child.firstName,
					lastName: child.lastName,
					grade: child.grade,
					childTotalAmount: child.childTotalAmount,
					selectedPrograms: child.selectedPrograms,
					birthday: moment(new Date(child.birthday)).format(),
					currentSchool: child.currentSchool,
					medicalInfo: child.medicalInfo,
					programPermission: child.programPermission.key,
					mediaRelease: child.mediaReleasePermission.key,
				});
			});

			let paymentInformation = {
				cardHolder: this.cardholderName.trim(),
				cardNumber: this.cardNumber.split(" ").join(""),
				expiryMonth: this.cardExpiry.split("/")[0],
				expiryYear: "20" + this.cardExpiry.split("/")[1],
				email: this.email,
				phone: this.phone,
				cvv: this.cvv,
				firstName: this.firstName,
				lastName: this.lastName,
				address: this.address,
				address2: this.address2,
				country: this.country,
				postalCode: this.postalCode_zip,
				city: this.city,
				region: this.selectedProvince.code,
				heardAboutProgram: this.heardAboutProgram,
			};

			// console.log({
			// 	formData: {
			// 		...this.store.Membership,
			// 		...this.store.ProgramSelection,
			// 		parentData: this.store.ParentInformation,
			// 		emergencyContactData: this.store.EmergencyContactInfo,
			// 		queryString: window.location.search,
			// 		dt: moment(new Date(Date.now())).format(),
			// 		sandbox: this.store.testMode,
			// 		campaignId: this.store.campaignId,
			// 		token: token,
			// 		amount: this.store.ProgramSelection.totalOfAllChildren,
			// 	},
			// 	paymentInformation,
			// 	childData,
			// });

			await axios
				.post("https://api.pllenty.com/girls-inc2/membership/process", {
					formData: {
						...this.store.Membership,
						...this.store.ProgramSelection,
						parentData: this.store.ParentInformation,
						emergencyContactData: this.store.EmergencyContactInfo,
						queryString: window.location.search,
						dt: moment(new Date(Date.now())).format(),
						sandbox: this.store.testMode,
						campaignId: this.store.campaignId,
						token: token,
						amount: this.store.ProgramSelection.totalOfAllChildren,
					},
					paymentInformation,
					childData,
				})
				.then((response) => {
					this.store.formSubmissionData = {
						status: response.data.transaction.status,
						merchantId: response.data.transaction.merchantId,
						amount: response.data.transaction.amount,
						errorMessage:
							response.data.transaction.message != undefined
								? response.data.transaction.message
								: null,
					};
				})
				.catch(() => {
					this.store.formSubmissionData = {
						errorMessage: "A network error has occured. Please try again.",
					};

					this.prevStep();
				});
		},
		prevStep() {
			this.$emit("prevStep");
		},
		async generateRecaptchaToken() {
			let recaptchaObject = this.store.recaptchaObject;
			const gtoken = await recaptchaObject.execute("pllentyForm");

			return gtoken;
		},
		maskedCardNumber() {
			let cardNumber = this.store.PaymentInformation.cardNumber;

			if (cardNumber) {
				return `${"*".repeat(cardNumber.length - 4)}${cardNumber.substr(
					cardNumber.length - 4
				)}`;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
	opacity: 0;
}
</style>
